import type { Changeset, SyncResponse } from './sync'
import type {
  AuditLogEntry,
  Autocomplete,
  AuthProviders,
  Dashboard,
  EquipmentItem,
  EquipmentItemInput,
  FixtureModel,
  FixtureModelInput,
  InventoryFilters,
  Location,
  LocationInput,
  Passkey,
  PasskeyChallenge,
  PasskeyInput,
  PasswordChangeInput,
  Session,
  SignInResult,
  SmsChallenge,
  StocktakeDetail,
  StocktakeSummary,
  TwoFactorStatus,
  User,
  UserInput,
  UserList,
} from './types'

/**
 * Thrown for any non-2xx response. `status` lets callers branch on 401
 * (sign-in expired) and 403 (not an admin) without parsing the message.
 */
export class ApiError extends Error {
  readonly status: number
  readonly violations: Record<string, string>

  constructor(status: number, message: string, violations: Record<string, string> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.violations = violations
  }
}

/**
 * The request never reached the API — no connection, a refused one, a DNS
 * failure (issue #64). An `ApiError` with status 0 rather than an error of its
 * own kind: everything downstream already branches on the status, and 0 is what
 * a response that does not exist is worth. It is deliberately not 500, which
 * would say the server answered and failed.
 */
export class OfflineError extends ApiError {
  constructor(cause?: unknown) {
    super(0, 'No connection — the Tech Inventory could not be reached.')
    this.name = 'OfflineError'
    if (cause !== undefined) this.cause = cause
  }
}

export interface ApiClientOptions {
  /**
   * Where the API lives. Defaults to a same-origin `/api`: the web app sits at
   * the front of the stack and everything under /api is proxied to Symfony.
   */
  baseUrl?: string
  /** Returns the bearer token, or null when signed out. */
  getToken: () => string | null
  /** Called when the API rejects the token, so the app can sign out. */
  onUnauthorized?: () => void
  fetchImpl?: typeof fetch
}

interface ErrorBody {
  error?: unknown
  message?: unknown
  violations?: unknown
}

function readViolations(body: ErrorBody): Record<string, string> {
  if (typeof body.violations !== 'object' || body.violations === null) return {}

  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(body.violations as Record<string, unknown>)) {
    if (typeof value === 'string') out[key] = value
  }
  return out
}

export class ApiClient {
  private readonly baseUrl: string
  private readonly getToken: () => string | null
  private readonly onUnauthorized?: () => void
  private readonly fetchImpl?: typeof fetch

  constructor(options: ApiClientOptions) {
    this.baseUrl = (options.baseUrl ?? '/api').replace(/\/$/, '')
    this.getToken = options.getToken
    this.onUnauthorized = options.onUnauthorized
    this.fetchImpl = options.fetchImpl
  }

  /**
   * Resolved per call rather than in the constructor: the client is created at
   * module scope, and not every environment has a global fetch by then.
   */
  private get doFetch(): typeof fetch {
    if (this.fetchImpl !== undefined) return this.fetchImpl

    if (typeof globalThis.fetch !== 'function') {
      throw new Error('No fetch implementation is available in this environment.')
    }

    return globalThis.fetch.bind(globalThis)
  }

  /**
   * Every request goes out through here so that being offline reads as one
   * thing everywhere (issue #64). `fetch` rejects with a bare `TypeError` for a
   * dropped connection, a refused one and a DNS failure alike, which is
   * indistinguishable from a programming mistake by its type alone — so the
   * translation happens once, here, rather than being guessed at from a message
   * by each of the three callers.
   *
   * Anything that is not a `TypeError` — an abort, say — is not the network
   * being absent and goes up untouched.
   */
  private async send(url: string, init: RequestInit): Promise<Response> {
    try {
      return await this.doFetch(url, init)
    } catch (cause) {
      if (cause instanceof TypeError) throw new OfflineError(cause)

      throw cause
    }
  }

  private authHeaders(): Record<string, string> {
    const token = this.getToken()

    return token === null ? {} : { Authorization: `Bearer ${token}` }
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', ...this.authHeaders() }

    if (body !== undefined) headers['Content-Type'] = 'application/json'

    const response = await this.send(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Stated rather than left to the default, which is already `same-origin`
      // in every current browser: the "trust this device" cookie (issue #16) is
      // set by one call and read by another, and a silent change of default
      // would turn that into a texted code on every sign-in with nothing to
      // point at. The client only ever talks to its own origin, so this grants
      // nothing a cross-origin call could use.
      credentials: 'same-origin',
    })

    if (response.status === 204) return undefined as T

    const text = await response.text()
    const parsed: unknown = text === '' ? null : JSON.parse(text)

    if (!response.ok) {
      // A rejected token means the session is over, whatever the caller was doing.
      if (response.status === 401) this.onUnauthorized?.()

      const errorBody = (parsed ?? {}) as ErrorBody
      const message =
        (typeof errorBody.error === 'string' ? errorBody.error : null) ??
        (typeof errorBody.message === 'string' ? errorBody.message : null) ??
        `Request failed with status ${response.status}`

      throw new ApiError(response.status, message, readViolations(errorBody))
    }

    return parsed as T
  }

  /**
   * A multipart POST, for the one thing this API takes that is not JSON
   * (issue #56). `Content-Type` is deliberately not set: the browser has to
   * write it itself so it can append the multipart boundary.
   *
   * The error handling below is `request()`'s, duplicated rather than
   * refactored around — pulling the two together would mean a request builder
   * branching on body type for one caller, and the shapes it has to produce
   * (a JSON string with a content type, a `FormData` without one) have nothing
   * in common but the headers.
   */
  private async upload<T>(path: string, file: Blob, occurredAt?: string): Promise<T> {
    const form = new FormData()
    form.append('file', file)

    // Only ever set on a replay (issue #64, step 4). It is what keeps the rule
    // that a non-null `occurredAt` in the trail *is* the marker that a change
    // came off a device — a photograph replayed hours later would otherwise be
    // the one change that quietly claimed to have happened at reconnection.
    if (occurredAt !== undefined) form.append('occurredAt', occurredAt)

    const response = await this.send(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', ...this.authHeaders() },
      body: form,
      credentials: 'same-origin',
    })

    const text = await response.text()
    const parsed: unknown = text === '' ? null : JSON.parse(text)

    if (!response.ok) {
      if (response.status === 401) this.onUnauthorized?.()

      const errorBody = (parsed ?? {}) as ErrorBody
      const message =
        (typeof errorBody.error === 'string' ? errorBody.error : null) ??
        `Upload failed with status ${response.status}`

      throw new ApiError(response.status, message, readViolations(errorBody))
    }

    return parsed as T
  }

  // — auth —

  /**
   * Email and password. Answers with a token, or with the second step that has
   * to be answered before there is one: a passkey where the account has
   * registered one (issue #15), otherwise a texted code where the installation
   * and the account can both use one (issue #16). No token exists in either
   * case, on the server either — the second step is where that sign-in is
   * minted.
   *
   * The branch is on `mfa`, which the API states rather than leaving to be
   * inferred from which fields turned up.
   */
  async login(email: string, password: string): Promise<SignInResult> {
    const body = await this.request<
      { mfa?: unknown; token?: string } & Partial<PasskeyChallenge> & Partial<SmsChallenge>
    >('POST', '/auth/login', { email, password })

    if (body.mfa === 'passkey' && typeof body.challengeToken === 'string') {
      return { mfa: 'passkey', challengeToken: body.challengeToken, publicKey: body.publicKey ?? {} }
    }

    if (body.mfa === 'sms' && typeof body.challengeToken === 'string') {
      return {
        mfa: 'sms',
        challengeToken: body.challengeToken,
        phoneHint: body.phoneHint ?? '',
        trustDeviceDays: body.trustDeviceDays ?? 0,
      }
    }

    if (typeof body.token !== 'string') {
      throw new ApiError(500, 'Sign-in did not return a token.')
    }

    return { mfa: null, token: body.token }
  }

  /**
   * Finishes a sign-in with the assertion the authenticator produced
   * (issue #15). The challenge token stands in for the bearer token there is
   * not one of yet, and is spent by this call either way — a refusal means
   * starting from the password again.
   */
  async completePasskeySignIn(
    challengeToken: string,
    credential: Record<string, unknown>,
  ): Promise<string> {
    const { token } = await this.request<{ token: string }>('POST', '/auth/passkey', {
      challengeToken,
      credential,
    })
    return token
  }

  /**
   * Finishes a sign-in with the code Twilio texted (issue #16). The challenge
   * token stands in for the bearer token there is not one of yet.
   *
   * Unlike the passkey call, a refusal here does **not** end the sign-in: a
   * mistyped digit is a mistyped digit, and the challenge survives a few of
   * them before it goes. `trustDevice` is the "Trust this device" box — the API
   * sets an HttpOnly cookie on the way out, which is why nothing is returned
   * about it and why this call has to carry credentials.
   */
  async completeSmsSignIn(
    challengeToken: string,
    code: string,
    trustDevice: boolean,
  ): Promise<string> {
    const { token } = await this.request<{ token: string }>('POST', '/auth/sms', {
      challengeToken,
      code,
      trustDevice,
    })
    return token
  }

  // — two-step verification (issue #16) —

  twoFactor(): Promise<TwoFactorStatus> {
    return this.request<TwoFactorStatus>('GET', '/two-factor')
  }

  /**
   * Untrusts every browser on this account, answering how many went. Takes the
   * current password for the reason the passkey endpoints do: changing what
   * signs you in means proving you are the one at the keyboard.
   */
  forgetTrustedDevices(currentPassword: string): Promise<{ forgotten: number }> {
    return this.request<{ forgotten: number }>('DELETE', '/two-factor/trusted-devices', {
      currentPassword,
    })
  }

  // — passkeys (issue #15) —

  async passkeys(): Promise<Passkey[]> {
    const { passkeys } = await this.request<{ passkeys: Passkey[] }>('GET', '/passkeys')
    return passkeys
  }

  /** Step one of adding one: prove it is you, and get the challenge. */
  passkeyRegistrationChallenge(currentPassword: string): Promise<PasskeyChallenge> {
    return this.request<PasskeyChallenge>('POST', '/passkeys/options', { currentPassword })
  }

  /** Step two: keep what the authenticator made. */
  async registerPasskey(input: PasskeyInput): Promise<Passkey> {
    const { passkey } = await this.request<{ passkey: Passkey }>('POST', '/passkeys', input)
    return passkey
  }

  /**
   * Removing one takes the current password too — the last passkey going takes
   * the second factor with it.
   */
  removePasskey(id: number, currentPassword: string): Promise<void> {
    return this.request<void>('DELETE', `/passkeys/${id}`, { currentPassword })
  }

  session(): Promise<Session> {
    return this.request<Session>('GET', '/auth/me')
  }

  /**
   * Your own account's history (issue #58). No id: the API reads it off the
   * token, so there is no version of this call that fetches somebody else.
   */
  async myAuditLog(): Promise<AuditLogEntry[]> {
    const { entries } = await this.request<{ entries: AuditLogEntry[] }>('GET', '/auth/audit-log')
    return entries
  }

  /**
   * Which social sign-in options are configured (issue #13). Answered without a
   * token — it is asked by the sign-in screen, before there is one.
   *
   * The Google flow itself is not a call this client can make: it is a chain of
   * browser redirects starting at `/api/auth/oauth/google/start`, because the
   * consent screen has to be somewhere the person can see it.
   */
  providers(): Promise<AuthProviders> {
    return this.request<AuthProviders>('GET', '/auth/providers')
  }

  /**
   * There is deliberately no `updateProfile` beside this (issue #52): the
   * account record, name included, is maintained by an admin through
   * `users`/`updateUser`. What the account holder changes for themselves is
   * their password and their passkeys.
   */
  changePassword(input: PasswordChangeInput): Promise<void> {
    return this.request<void>('POST', '/auth/change-password', input)
  }

  requestPasswordReset(email: string): Promise<void> {
    return this.request<void>('POST', '/auth/forgot-password', { email })
  }

  resetPassword(token: string, password: string): Promise<void> {
    return this.request<void>('POST', '/auth/reset-password', { token, password })
  }

  // — inventory —

  async items(filters: InventoryFilters = {}): Promise<EquipmentItem[]> {
    const params = new URLSearchParams()
    if (filters.q !== undefined && filters.q !== '') params.set('q', filters.q)
    if (filters.category !== undefined && filters.category !== 'All') {
      params.set('category', filters.category)
    }
    if (filters.locationId !== undefined && filters.locationId !== 'All') {
      params.set('locationId', String(filters.locationId))
    }
    if (filters.fixtureModelId !== undefined && filters.fixtureModelId !== 'All') {
      params.set('fixtureModelId', String(filters.fixtureModelId))
    }

    const query = params.toString()
    const { items } = await this.request<{ items: EquipmentItem[]; total: number }>(
      'GET',
      query === '' ? '/items' : `/items?${query}`,
    )
    return items
  }

  item(id: number): Promise<EquipmentItem> {
    return this.request<EquipmentItem>('GET', `/items/${id}`)
  }

  createItem(input: EquipmentItemInput): Promise<EquipmentItem> {
    return this.request<EquipmentItem>('POST', '/items', input)
  }

  updateItem(id: number, input: Partial<EquipmentItemInput>): Promise<EquipmentItem> {
    return this.request<EquipmentItem>('PATCH', `/items/${id}`, input)
  }

  /** Moves an item to the trash; the record survives and can be restored. */
  deleteItem(id: number): Promise<void> {
    return this.request<void>('DELETE', `/items/${id}`)
  }

  // — trash (issue #8) —

  async trash(): Promise<EquipmentItem[]> {
    const { items } = await this.request<{ items: EquipmentItem[]; total: number }>(
      'GET',
      '/items/trash',
    )
    return items
  }

  restoreItem(id: number): Promise<EquipmentItem> {
    return this.request<EquipmentItem>('POST', `/items/trash/${id}/restore`)
  }

  /** Destroys one trashed record for good. Admin only. */
  purgeItem(id: number): Promise<void> {
    return this.request<void>('DELETE', `/items/trash/${id}`)
  }

  /** Empties the trash, answering with how many records went. Admin only. */
  emptyTrash(): Promise<{ purged: number }> {
    return this.request<{ purged: number }>('DELETE', '/items/trash')
  }

  autocomplete(): Promise<Autocomplete> {
    return this.request<Autocomplete>('GET', '/items/autocomplete')
  }

  // — photographs (issue #56) —

  /**
   * Puts a photograph on an item, answering with the item as it now reads.
   * Replaces whatever was there, and gives the item its own picture even if it
   * was showing its fixture model's — that model, and every other lantern
   * linked to it, is untouched.
   */
  setItemImage(id: number, file: Blob, occurredAt?: string): Promise<EquipmentItem> {
    return this.upload<EquipmentItem>(`/items/${id}/image`, file, occurredAt)
  }

  /**
   * Removes an item's **override**, dropping it back to its fixture model's
   * photograph. It never reaches through to the model's picture, which every
   * other lantern of that kind is also using.
   */
  removeItemImage(id: number, occurredAt?: string): Promise<EquipmentItem> {
    return this.request<EquipmentItem>('DELETE', `/items/${id}/image${when(occurredAt)}`)
  }

  setFixtureModelImage(id: number, file: Blob, occurredAt?: string): Promise<FixtureModel> {
    return this.upload<FixtureModel>(`/fixture-models/${id}/image`, file, occurredAt)
  }

  removeFixtureModelImage(id: number, occurredAt?: string): Promise<FixtureModel> {
    return this.request<FixtureModel>('DELETE', `/fixture-models/${id}/image${when(occurredAt)}`)
  }

  /**
   * Fetches a photograph as a blob.
   *
   * It has to come through here rather than being an `<img src>` because the
   * API is authorised by a bearer token and an image element sends no headers.
   * The caller turns this into an object URL — see `useImageObjectUrl` — and
   * revokes it when the element goes.
   */
  async imageBlob(url: string): Promise<Blob> {
    // Taken as given rather than joined to `baseUrl`: the API builds this path
    // whole (`/api/items/3/image`), because it is meant to work as an ordinary
    // URL and the app is same-origin with no per-environment API base.
    const response = await this.send(url, {
      headers: this.authHeaders(),
      credentials: 'same-origin',
    })

    if (!response.ok) {
      if (response.status === 401) this.onUnauthorized?.()
      throw new ApiError(response.status, 'That photograph could not be loaded.')
    }

    return response.blob()
  }

  /**
   * Replays a queue of changes made offline (issue #64), whole and in order.
   *
   * One call rather than a march through the individual endpoints: a device
   * that has just come back into signal is the last thing that should be asked
   * to sequence dozens of round trips, and the server needs to see the batch as
   * one unit of work to resolve a create and the edit that points at it.
   */
  sync(changesets: readonly Changeset[]): Promise<SyncResponse> {
    return this.request<SyncResponse>('POST', '/sync', { changesets })
  }

  async auditLog(itemId: number): Promise<AuditLogEntry[]> {
    const { entries } = await this.request<{ entries: AuditLogEntry[] }>(
      'GET',
      `/items/${itemId}/audit-log`,
    )
    return entries
  }

  // — fixture models —

  async fixtureModels(): Promise<FixtureModel[]> {
    const { fixtureModels } = await this.request<{ fixtureModels: FixtureModel[] }>(
      'GET',
      '/fixture-models',
    )
    return fixtureModels
  }

  fixtureModel(id: number): Promise<FixtureModel> {
    return this.request<FixtureModel>('GET', `/fixture-models/${id}`)
  }

  createFixtureModel(input: FixtureModelInput): Promise<FixtureModel> {
    return this.request<FixtureModel>('POST', '/fixture-models', input)
  }

  updateFixtureModel(id: number, input: Partial<FixtureModelInput>): Promise<FixtureModel> {
    return this.request<FixtureModel>('PATCH', `/fixture-models/${id}`, input)
  }

  deleteFixtureModel(id: number): Promise<void> {
    return this.request<void>('DELETE', `/fixture-models/${id}`)
  }

  // — locations —

  async locations(): Promise<Location[]> {
    const { locations } = await this.request<{ locations: Location[] }>('GET', '/locations')
    return locations
  }

  createLocation(input: LocationInput): Promise<Location> {
    return this.request<Location>('POST', '/locations', input)
  }

  updateLocation(id: number, input: Partial<LocationInput>): Promise<Location> {
    return this.request<Location>('PATCH', `/locations/${id}`, input)
  }

  /**
   * Moves a location to the trash. Its kit stays attached and comes back with
   * it on a restore; until then that kit reads as unassigned.
   */
  deleteLocation(id: number): Promise<void> {
    return this.request<void>('DELETE', `/locations/${id}`)
  }

  // — location trash, admin only (issue #9) —

  async locationTrash(): Promise<Location[]> {
    const { locations } = await this.request<{ locations: Location[]; total: number }>(
      'GET',
      '/locations/trash',
    )
    return locations
  }

  restoreLocation(id: number): Promise<Location> {
    return this.request<Location>('POST', `/locations/trash/${id}/restore`)
  }

  /** Destroys one binned location for good, detaching whatever kit was on it. */
  purgeLocation(id: number): Promise<void> {
    return this.request<void>('DELETE', `/locations/trash/${id}`)
  }

  emptyLocationTrash(): Promise<{ purged: number }> {
    return this.request<{ purged: number }>('DELETE', '/locations/trash')
  }

  /**
   * Rewrites the walking order (issue #27), taking the ids in the order they
   * should be walked. Answers with the whole list, already ordered, so the
   * caller replaces its cache from the response rather than refetching.
   */
  async reorderLocations(ids: number[]): Promise<Location[]> {
    const { locations } = await this.request<{ locations: Location[] }>('PUT', '/locations/order', {
      order: ids,
    })
    return locations
  }

  // — dashboard —

  dashboard(): Promise<Dashboard> {
    return this.request<Dashboard>('GET', '/dashboard')
  }

  // — stocktake —

  stocktakes(): Promise<{ open: StocktakeSummary | null; history: StocktakeSummary[] }> {
    return this.request('GET', '/stocktakes')
  }

  stocktake(id: number): Promise<StocktakeDetail> {
    return this.request<StocktakeDetail>('GET', `/stocktakes/${id}`)
  }

  startStocktake(locationIds: number[] = []): Promise<StocktakeDetail> {
    return this.request<StocktakeDetail>('POST', '/stocktakes', { locationIds })
  }

  countLine(
    stocktakeId: number,
    lineId: number,
    input: { countedQuantity?: number | null; note?: string | null },
  ): Promise<import('./types').StocktakeLine> {
    return this.request('PATCH', `/stocktakes/${stocktakeId}/lines/${lineId}`, input)
  }

  completeStocktake(id: number, applyCounts = true): Promise<StocktakeDetail> {
    return this.request<StocktakeDetail>('POST', `/stocktakes/${id}/complete`, { applyCounts })
  }

  abandonStocktake(id: number): Promise<StocktakeSummary> {
    return this.request<StocktakeSummary>('POST', `/stocktakes/${id}/abandon`)
  }

  // — users (admin only) —

  /**
   * The account list, and whether this installation can text at all
   * (issue #58). `smsConfigured` comes back beside the rows rather than on
   * each: it is one fact about the deployment, and with Twilio off every
   * account would otherwise read "None" with no way to say why.
   */
  users(): Promise<UserList> {
    return this.request<UserList>('GET', '/users')
  }

  /**
   * One account's history (issue #58) — sign-ins, refusals, and every change
   * made to the record. Admin only; the account holder reads their own through
   * `myAuditLog()`.
   */
  async userAuditLog(id: number): Promise<AuditLogEntry[]> {
    const { entries } = await this.request<{ entries: AuditLogEntry[] }>(
      'GET',
      `/users/${id}/audit-log`,
    )
    return entries
  }

  user(id: number): Promise<User> {
    return this.request<User>('GET', `/users/${id}`)
  }

  createUser(input: UserInput): Promise<User> {
    return this.request<User>('POST', '/users', input)
  }

  updateUser(id: number, input: Partial<UserInput>): Promise<User> {
    return this.request<User>('PATCH', `/users/${id}`, input)
  }

  deleteUser(id: number): Promise<void> {
    return this.request<void>('DELETE', `/users/${id}`)
  }

  resendInvite(id: number): Promise<void> {
    return this.request<void>('POST', `/users/${id}/resend-invite`)
  }
}

/**
 * When a change was made, as a query string, for the two endpoints that take
 * bytes rather than JSON and so have nowhere else to put it (issue #64,
 * step 4). Empty for everything done at a desk, which is almost everything.
 */
function when(occurredAt?: string): string {
  return occurredAt === undefined ? '' : `?occurredAt=${encodeURIComponent(occurredAt)}`
}
